import React, { useState, useEffect, useMemo } from "react";
import { TbX, TbAlertTriangle, TbArrowBackUp } from "react-icons/tb";
import { Button, Field } from "../../components";
import IconPicker, { CategoryIcon } from "./IconPicker";
import {
  TIERS, TIER_TONE, OPERATORS, isBetween,
  describeRule, checkBands, toPayload, sameRules,
} from "../../lib/thresholds";
import "./Settings.css";

// Where a scoring band is actually changed.
//
// A drawer rather than a modal on purpose: changing one category's bands is usually a
// judgement made by comparing it against its neighbours, and a modal would black out the
// table you are comparing against.
//
// The default is kept on screen the whole time. Someone re-banding a category needs to
// know what they are departing from, and the "back to default" affordance is worthless
// if you cannot see what default means.
const ThresholdDrawer = ({ category, groups = [], isAdmin, onClose, onSave, onReset, onMove, onUpdate }) => {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [moving, setMoving] = useState(false);
  const [iconBusy, setIconBusy] = useState(false);

  useEffect(() => {
    // Deep-ish copy: the tier objects are edited field by field, and mutating the row
    // the table is rendering from would make the drawer's changes appear behind it
    // before anything was saved.
    const rules = category?.rules || {};
    setDraft(Object.fromEntries(TIERS.map((t) => [t, { ...(rules[t] || {}) }])));
    setConfirmReset(false);
  }, [category?.id, category?.rules]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const defaults = category?.default_rules || {};
  const problems = useMemo(() => (draft ? checkBands(draft) : []), [draft]);
  const unchanged = useMemo(
    () => (draft ? sameRules(draft, category?.rules) : true),
    [draft, category?.rules]
  );

  if (!category || !draft) return null;

  const setTier = (tier, patch) =>
    setDraft((d) => ({ ...d, [tier]: { ...d[tier], ...patch } }));

  // Switching to or from Between swaps which numeric fields mean anything. The old ones
  // are left in place rather than cleared, so toggling back does not lose what was typed.
  const setOperator = (tier, operator) => setTier(tier, { operator });

  const save = async () => {
    setBusy(true);
    try {
      const ok = await onSave(category.id, toPayload(draft));
      if (ok) onClose();
    } finally { setBusy(false); }
  };

  // PUT /categories/:id writes default_thresholds, icon and scan_type together with no
  // COALESCE, so an icon-only change has to resend the other two verbatim or it blanks
  // them. `default_rules` is the GLOBAL default set the API resolved this category from —
  // not this workspace's bands, which live behind the Save button below.
  const saveIcon = async (icon) => {
    const defaults = category.default_rules;
    // Refuse rather than send an empty object: that would overwrite the platform-wide
    // default bands for this category with nothing.
    if (!defaults || !TIERS.every((t) => defaults[t])) return;
    setIconBusy(true);
    try {
      await onUpdate(category.id, {
        default_thresholds: defaults,
        icon: icon || null,
        scan_type: category.scan_type || null,
      });
    } finally { setIconBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const ok = await onReset(category.id);
      if (ok) onClose();
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="v2-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="v2-drawer v2-drawer--wide" role="dialog" aria-label={`${category.name} thresholds`}>
        <div className="v2-drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 className="v2-thr-drawertitle">
              <CategoryIcon name={category.icon} size={16} />
              {category.name}
            </h4>
            <div className="v2-drawer-sub">
              {category.scan_type}
              {category.metric ? ` · ${category.metric}` : ""}
            </div>
          </div>
          <span className={`v2-rbadge${category.mode === "custom" ? " v2-rbadge--accent" : ""}`}>
            {category.mode === "custom" ? "CUSTOMISED" : "DEFAULT"}
          </span>
          <Button icon aria-label="Close" onClick={onClose}><TbX size={15} /></Button>
        </div>

        <div className="v2-drawer-body">
          {category.description && <p className="v2-thr-desc">{category.description}</p>}

          {category.data_source && (
            <div className="v2-thr-source">
              <span className="v2-thr-source-label">Measured from</span>
              <code>{category.data_source}</code>
            </div>
          )}

          {/* Which group a category sits in is a property of the category, so it is edited
              here rather than from the group header — moving one out is the same action as
              moving one in, and doing it from both ends would be two ways to say one thing.

              Saved on change, not with the bands below: it writes the shared catalogue,
              while Save writes only this workspace's bands. Batching two different scopes
              behind one button is how someone changes more than they meant to. */}
          {isAdmin && (onMove || onUpdate) && (
            <div className="v2-grid2">
              {onMove && (
                <Field label="Scan group"
                       hint="Applies to every workspace, and takes effect immediately.">
                  <select className="v2-input" value={category.scan_type || ""}
                          aria-label="Scan group" disabled={moving}
                          onChange={async (e) => {
                            setMoving(true);
                            await onMove(category.id, e.target.value);
                            setMoving(false);
                          }}>
                    <option value="">Uncategorised</option>
                    {groups.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                  </select>
                </Field>
              )}

              {/* Same scope and the same immediate save as the group above: the icon is a
                  property of the shared category, not of this workspace's bands, so it
                  must not ride along on the Save button below. */}
              {onUpdate && (
                <div className={iconBusy ? "v2-iconbusy" : undefined}>
                  <IconPicker value={category.icon || ""} onChange={saveIcon} />
                </div>
              )}
            </div>
          )}

          {TIERS.map((tier) => {
            const rule = draft[tier];
            const between = isBetween(rule);
            const def = defaults[tier];
            return (
              <div key={tier} className="v2-thr-tier">
                <div className="v2-thr-tier-head">
                  <span className={`v2-rbadge v2-rbadge--${TIER_TONE[tier]}`}>{tier.toUpperCase()}</span>
                  <span className="v2-thr-preview">{describeRule(rule)}</span>
                </div>

                <div className="v2-thr-controls">
                  <Field label="Condition">
                    <select className="v2-input" value={rule.operator || ""}
                            aria-label={`${tier} operator`}
                            onChange={(e) => setOperator(tier, e.target.value)}>
                      <option value="">Not set</option>
                      {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>

                  {between ? (
                    <>
                      <Field label="From">
                        <input className="v2-input" type="number" value={rule.lower ?? ""}
                               aria-label={`${tier} lower bound`}
                               onChange={(e) => setTier(tier, { lower: e.target.value })} />
                      </Field>
                      <Field label="To">
                        <input className="v2-input" type="number" value={rule.upper ?? ""}
                               aria-label={`${tier} upper bound`}
                               onChange={(e) => setTier(tier, { upper: e.target.value })} />
                      </Field>
                    </>
                  ) : (
                    <Field label="Value">
                      <input className="v2-input" type="number" value={rule.value ?? ""}
                             aria-label={`${tier} value`}
                             disabled={!rule.operator}
                             onChange={(e) => setTier(tier, { value: e.target.value })} />
                    </Field>
                  )}
                </div>

                {def && (
                  <div className="v2-thr-default">
                    Default: {describeRule(def)}
                  </div>
                )}
              </div>
            );
          })}

          {/* Advisory, not blocking. A gap or an overlap is a policy decision this screen
              is not entitled to veto — but it must not let one happen silently either. */}
          {problems.length > 0 && (
            <div className="v2-thr-warn">
              <TbAlertTriangle size={15} />
              <div>
                <b>Check these bands</b>
                <ul>
                  {problems.map((p, i) => <li key={i}>{p.text}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="v2-drawer-foot">
          {category.mode === "custom" && (
            confirmReset ? (
              <>
                <span className="v2-hint" style={{ flex: 1 }}>
                  Drop this workspace's bands and follow the default again?
                </span>
                <Button onClick={() => setConfirmReset(false)} disabled={busy}>Cancel</Button>
                <Button variant="danger" onClick={reset} disabled={busy}>
                  {busy ? "Resetting…" : "Reset"}
                </Button>
              </>
            ) : (
              <Button onClick={() => setConfirmReset(true)} disabled={busy}>
                <TbArrowBackUp size={14} style={{ verticalAlign: "-2px" }} /> Back to default
              </Button>
            )
          )}
          {!confirmReset && (
            <>
              <span style={{ flex: 1 }} />
              <Button onClick={onClose} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={save} disabled={busy || unchanged}>
                {busy ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default ThresholdDrawer;
